import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import {
  Box,
  Text,
  Flex,
  IconButton,
  Badge,
  Tooltip,
  useColorModeValue,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverHeader,
  PopoverCloseButton,
  Portal
} from "@chakra-ui/react";
import { FiFilter, FiColumns, FiBarChart2, FiActivity, FiTrash2, FiPlus } from "react-icons/fi";
import { getFilterDisplayString } from "utils/nameUtils";
import { KGStepInterface } from "./Interface";
import { useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";
import KGRecommendations from "./KGRecommendations";

interface ICustomNodeProps {
  data: KGStepInterface;
  selected?: boolean;
}

const KGAnalysisStepNode = ({ data: currentNode, selected }: ICustomNodeProps) => {
  const { 
    deleteNode, 
    handleAddChildNode,
    openRecommendationNodeId,
    openRecommendationPopover,
    closeRecommendationPopover,
    setSelectedNodeId
  } = useKGAnalysisPlanContext();
  
  // Check if this node's recommendation popover is open
  const isRecommendationOpen = openRecommendationNodeId === currentNode.id;

  const getNodeColor = (type?: string) => {
    switch (type) {
      case "metric":
        return "green";
      default:
        return "green";
    }
  };

  // Count of child nodes from all recommendations
  const getTotalChildCount = (): number => {
    if (!currentNode.recommendation_children_map) return 0;
    let count = 0;
    
    // Convert to Array.from to avoid TypeScript errors with Map iteration
    Array.from(currentNode.recommendation_children_map.entries()).forEach(([_, childIds]) => {
      count += childIds.length;
    });
    
    return count;
  };

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = selected ? getNodeColor(currentNode.type) : "gray.200";
  
  // Handle delete node
  const handleDelete = (event: React.MouseEvent) => {
    // Stop event from propagating to parent (node click)
    event.stopPropagation();
    deleteNode(currentNode.id);
  };
  
  // Handle add recommendation - prevent event propagation
  const handleAddRecommendation = (event: React.MouseEvent) => {
    closeRecommendationPopover();
    event.stopPropagation();
    openRecommendationPopover(currentNode.id);
    setSelectedNodeId(currentNode?.id);
  };
  
  // Don't show delete button for root nodes
  const showDeleteButton = currentNode.type !== "metric";

  // Add a child node to a recommendation
  const addChildNode = async (recommendationIndex: number) => {
    if (!currentNode) return;
    await handleAddChildNode(recommendationIndex, currentNode);
  };

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor={borderColor}
      bg={bgColor}
      boxShadow="sm"
      width="200px"
      height="50px"
      position="relative"
      _hover={{ boxShadow: "md" }}
      zIndex={selected ? 10 : 1}
    >
      {/* Top Row with Controls */}
      <Flex
        position="absolute"
        top="2px"
        left="2px"
        right="2px"
        height="18px"
        alignItems="center"
        justifyContent="space-between"
        zIndex="2"
      >
        {/* Left: Metric name */}
        <HStack spacing={1}>
          {currentNode?.metrics && currentNode?.metrics?.length > 0 && (
            <Tooltip label={`Metric: ${currentNode.selected_metrics?.map((metric) => metric.header?.name).join(", ")}`} placement="top">
              <Badge
                fontSize="3xs"
                px={1}
                colorScheme={getNodeColor(currentNode.type)}
                display="flex"
                alignItems="center"
                height="16px"
                isTruncated={true}
                overflow="hidden"
                maxW={110}
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                <Box as={FiBarChart2} size="8px" mr="1px" />
                {currentNode.selected_metrics?.map((metric) => metric.header?.name).join(", ")}
              </Badge>
            </Tooltip>
          )}
        </HStack>

        {/* Right: Filters and Columns */}
        <HStack spacing={1}>
          {/* Display selected columns */}
          {currentNode.selected_columns && currentNode.selected_columns.length > 0 && (
            <Tooltip
              label={`Columns: ${currentNode.selected_columns
                .map((col) => col?.kg_column_header?.name || "Unnamed")
                .join(", ")}`}
              placement="top"
            >
              <IconButton
                aria-label="Columns"
                icon={<FiColumns size="10px" />}
                size="xs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                minW="16px"
                height="16px"
                p={0}
              />
            </Tooltip>
          )}
          
          {/* Display filters */}
          {currentNode.selected_filters && currentNode.selected_filters.length > 0 && (
            <Tooltip
              label={`Filters: ${currentNode.selected_filters
                .map((filter) => getFilterDisplayString(filter))
                .join(", ")}`}
              placement="top"
            >
              <IconButton
                aria-label="Filters"
                icon={<FiFilter size="10px" />}
                size="xs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                minW="16px"
                height="16px"
                p={0}
              />
            </Tooltip>
          )}

          {/* Display benchmarks */}
          {currentNode.selected_benchmarks && currentNode.selected_benchmarks.length > 0 && (
            <Tooltip
              label={`Benchmarks: ${currentNode.selected_benchmarks
                .map((benchmark) => benchmark.header?.name)
                .join(", ")}`}
              placement="top"
            >
              <IconButton
                aria-label="Benchmarks"
                icon={<FiActivity size="10px" />}
                size="xs"
                variant="ghost"
                colorScheme={getNodeColor(currentNode.type)}
                minW="16px"
                height="16px"
                p={0}
              />
            </Tooltip>
          )}
          
          {/* Delete button */}
          {showDeleteButton && (
            <Tooltip label="Delete node and its subtree" placement="top">
              <IconButton
                aria-label="Delete Node"
                icon={<FiTrash2 size="10px" />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                minW="16px"
                height="16px"
                p={0}
                onClick={handleDelete}
              />
            </Tooltip>
          )}
        </HStack>
      </Flex>

      {/* Node Label Center Layer */}
      <Flex
        position="absolute"
        top="20px"
        left="0"
        right="0"
        bottom="0"
        px={2}
        alignItems="center"
        justifyContent="center"
        zIndex="1"
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          color="gray.700"
          textAlign="center"
          noOfLines={1}
        >
          {currentNode.label}
        </Text>
      </Flex>

      {/* Bottom Left - Recommendation indicator */}
      {currentNode?.recommendation_rules && currentNode?.recommendation_rules?.length > 0 && (
        <Tooltip
          label={`Recommendations: ${currentNode?.recommendation_rules?.length}, Child Nodes: ${getTotalChildCount()}`}
          placement="bottom"
        >
          <IconButton
            aria-label="Recommendations"
            icon={<FiColumns size="10px" />}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            position="absolute"
            bottom="1px"
            left="1px"
            zIndex="2"
            minW="16px"
            height="16px"
            p={0}
          />
        </Tooltip>
      )}

      {/* Bottom Right - Add Recommendation Button */}
      <Popover
        isOpen={isRecommendationOpen}
        onClose={closeRecommendationPopover}
        placement="start-start"
        closeOnBlur={false}
        closeOnEsc={true}
        isLazy={true}
        strategy="fixed"
      >
        <PopoverTrigger>
          <Tooltip label="Add recommendation and child node" placement="bottom">
            <IconButton
              aria-label="Add Recommendation"
              icon={<FiPlus size="10px" />}
              size="xs"
              variant="ghost"
              colorScheme="green"
              position="absolute"
              bottom="1px"
              right="1px"
              zIndex="2"
              minW="16px"
              height="16px"
              p={0}
              onClick={(e) => {
                e.stopPropagation();
                handleAddRecommendation(e);
              }}
              _hover={{ bg: "green.100" }}
            />
          </Tooltip>
        </PopoverTrigger>
        <Portal>
          <PopoverContent 
            width="400px" 
            maxWidth="400px" 
            maxHeight="400px"
            overflowY="auto"
            zIndex={9999}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            boxShadow="2xl"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            position="absolute"
            mt={2}
            _focus={{ boxShadow: "outline" }}
          >
          <PopoverHeader>
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <Text fontSize="md" fontWeight="bold" color="gray.800" mb={0}>
                  Add Recommendation
                </Text>
                <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={0}>
                  for "{currentNode.label}"
                </Text>
              </Box>
              <PopoverCloseButton position="relative" top={0} right={0} />
            </Flex>
          </PopoverHeader>
          <PopoverBody>
            <KGRecommendations 
              currentNode={currentNode} 
              addChildToRecommendation={addChildNode} 
            />
          </PopoverBody>
        </PopoverContent>
        </Portal>
      </Popover>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "#718096",
          width: "4px",
          height: "4px",
          zIndex: 3,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "#718096",
          width: "4px",
          height: "4px",
          zIndex: 3,
        }}
      />
    </Box>
  );
};

export default memo(KGAnalysisStepNode); 